import { convertNewlines } from 'lib/util';
import { uuid } from '../../../shared/lib/uuid.js';
import { appUI } from 'lib/app-ui';
import { pui } from 'lib/perspectives-ui';
import { WIDTHS } from 'lib/constants';
import { Node, Link, Region } from '../store';
import { escapeRegExp } from 'lib/shared-util';
import htmlEscape from 'escape-html';
import Mark from 'mark.js';

class SearchUI {

  constructor() {
    this.currentQuery = '';
    this.searchResults = [];
    this.resultCountsPerParent = {};
    this.resultIndex = -1;
    this.focusedElement = null;
  }

  setCanvas(canvas) {
    this.canvas = canvas;
  }

  setSearchInputElement(element) {
    this.searchInput = element;
  }

  setSearchModal(tag) {
    this.searchModal = tag;
  }

  // refresh search results if we're in search mode
  refreshResults() {
    if (this.searchModal && this.searchModal.open && this.currentQuery.length > 0) {
      this.generateResults();
    }
  }


  unsetFocusedResult() {
    this.resultIndex = -1;
    this.focusedElement = null;
    $('.search-result-focused').removeClass('search-result-focused');
    $('.search-result-parent-focused').removeClass('search-result-parent-focused');
    $('.search-result-label').text('');
  }

  nodeMatches({ node, searchRegEx, query, field } ) {

    if (!field) {
      // search all fields
      if ((node.title && node.title.match(searchRegEx)) ||
          (node.summary && node.summary.match(searchRegEx)) ||
          (pui.getNodePerspectives(node).length && this.matchAnnotations(node,searchRegEx)) ||
          (node.hasAttributes() && this.getMatchedAttributes({node,query,searchRegEx}).length)) {
        return true;
      }
    }

    if (field === 'title' && (node.title && node.title.match(searchRegEx))) {
      return true;
    }

    if (field === 'summary' && (node.summary && node.summary.match(searchRegEx))) {
      return true;
    }
  }

  regionMatches({ region, searchRegEx }) {
    if (region.title && region.title.match(searchRegEx)) return true;
    return false;
  }

  matchAnnotations(node,searchRegEx) {
    if (this.getMatchingAnnotations(node,searchRegEx).length) {
      return true;
    }
    return false;
  }

  getMatchedAttributes({node,query,searchRegEx}) {
    const {key,value} = this.getKeyValueFilter(query);
    let keyRegEx;
    let valueRegEx;

    // if user typed something like "status:done", then split those out
    if (key) {
      keyRegEx = this.getSearchRegex(key);
      valueRegEx = this.getSearchRegex(value);
    }
    const matches = [];
    for (const role in node.attributes) {

      if (node.attributes[role] && node.attributes[role].length) {
        /* return true as soon as we find one matching attribute name/value */
        for (const attr of node.attributes[role]) {
          const fieldName = attr.displayName || attr.name;
          if (keyRegEx && valueRegEx) {
            // if user typed something like "status:done"
            if (fieldName.match(keyRegEx) && attr.value.match(valueRegEx)) {
              matches.push(attr);
            }
          } else {
            // if user typed "done"
            if (fieldName.match(searchRegEx) || attr.value.match(searchRegEx)) {
              matches.push(attr);
            }
          }
        }
      }
    }
    return matches;
  }

  getKeyValueFilter(query) {
    const parts = query.split(/\:/,2);

    if (parts.length !== 2) {
      if (query[0] === '#') {
        return {
          key: '#',
          value: query.slice(1),
        };
      } else {
        return { key: null, value: null };
      }
    }

    return {
      key: parts[0].trim(),
      value: parts[1].trim(),
    };
  }

  getMatchingAnnotations(node,searchRegEx) {
    const matches = [];
    const perspectives = pui.getNodePerspectives(node);
    for (const perspective of perspectives) {
      const annotation = perspective.getAnnotation(node.id);
      if (annotation) {
        if (annotation.match(searchRegEx)) {
          matches.push(perspective);
        }
      }
    }
    return matches;
  }

  // given a string, get all nodes with a matching title
  titleSearch(query) {
    const searchRegEx = this.getSearchRegex(query);
    const results = [];
    for (const [id, node] of this.canvas.store.nodes) {
      if (node.title.match(searchRegEx)) {
        results.push(node);
      }
    }
    results.sort((a,b) => {
      const aLen = a.title ? a.title.length : 0;
      const bLen = b.title ? b.title.length : 0;
      return aLen - bLen;
    });
    return results;
  }

  generateResults() {
    $('.search-result').removeClass('search-result');
    $('.search-result-parent').removeClass('search-result-parent');
    $('.node-attribute--search-result').removeClass('node-attribute--search-result');
    const input = this.searchInput.value;
    // if we're running a new query, reset the result index
    if (input !== this.currentQuery) {
      this.unsetFocusedResult();
    }
    this.currentQuery = input;
    this.unhighlightAll(this.searchResults);
    this.searchResults = [];
    this.resultCountsPerParent = {};
    if (input == '') {
      return;
    };
    const { searchRegEx, query, field } = this.getQueryDetails();

    for (const [id, node] of this.canvas.store.nodes) {
      if (this.nodeMatches({ node, searchRegEx, query, field } )) {
        this.searchResults.push(node);
        this.styleResultNode(node);
      }
    }

    for (const region of this.canvas.store.getRegions()) {
      if (this.regionMatches({ region, searchRegEx })) {
        this.searchResults.push(region);
        this.styleResultRegion(region);
      }
    }

    const topLevelResults = {};
    const depth = {};
    const firstVisibleAncestor = {};
    const orderIndexSum = {};
    const actualOrderIndexes = {};

    // get a score so we can sort results so we iterate over them depth-first
    for (const element of this.searchResults) {
      if (element instanceof Node) {
        const n = element;
        const lineage = n.getLineage();
        topLevelResults[n.id] = lineage[0];
        depth[n.id] = lineage.length;
        const firstVisibleNode = n.getFirstVisibleAncestorNode();
        firstVisibleAncestor[n.id] = firstVisibleNode;
        if (firstVisibleNode.id !== n.id) {
          // count up the number of matching children underneath a visible parent
          if (!this.resultCountsPerParent[ firstVisibleNode.id ]) {
            this.resultCountsPerParent[ firstVisibleNode.id ] = 0;
          }
          this.resultCountsPerParent[ firstVisibleNode.id ]++;
        }
        orderIndexSum[n.id] = 0;
        let currentDepth = 0;


        if (firstVisibleNode.parentId && firstVisibleNode.getGoverningLayoutName() !== 'layout-freehand') {
          for (const _n of lineage) {
            if (!actualOrderIndexes[_n.id]) {
              if (_n.getParent()) {
                // orderIndex param is relative and unpredictable, so get an actual integer that's consistent...
                actualOrderIndexes[_n.id] = _n.getParent().children().findIndex(c => c.id === _n.id) + 1;
              } else {
                actualOrderIndexes[_n.id] = 1;
              }
            }
            const actualOrderIndex = actualOrderIndexes[_n.id] || 1;
            orderIndexSum[n.id] += (actualOrderIndex / Math.pow(10,currentDepth));
            currentDepth++;
          }
        } else {
          topLevelResults[n.id] = firstVisibleNode;
        }
      } else if (element instanceof Region) {
        topLevelResults[element.id] = {
          absolutePosition: {
            x: element.left,
            y: element.top,
          }
        };
        orderIndexSum[element.id] = 0;
      }
    }

    // sort results from top left to bottom right of the canvas, and parent to child, and siblings by orderIndex
    this.searchResults.sort((a,b) =>
      10000 * (topLevelResults[a.id].absolutePosition.x - topLevelResults[b.id].absolutePosition.x) +
      1000 * (topLevelResults[a.id].absolutePosition.y - topLevelResults[b.id].absolutePosition.y) +
      orderIndexSum[a.id] - orderIndexSum[b.id]
    );

    if (this.resultIndex >= 0 && this.searchResults.length && this.searchResults[this.resultIndex]) {
      this.setFocusedHighlight(this.searchResults[this.resultIndex]);
    }
    this.searchModal.update();
  }

  getQueryDetails() {
    const prefixMatch = this.currentQuery.match(/^(title|summary|tag)\:/);
    let query = this.currentQuery;
    let field;
    if (prefixMatch && prefixMatch.length) {
      field = prefixMatch[1];
      query = this.currentQuery.replace(/^(title|summary|tag)\:/,'');
    }
    const searchRegEx = this.getSearchRegex(query);
    return {searchRegEx,query,field};
  }

  getSearchRegex(query) {
    const safeQuery = escapeRegExp(query);
    const searchRegEx = new RegExp(`.*${safeQuery}.*`, 'i');
    return searchRegEx;
  }

  // find the first result in the current viewport set it as the next resule
  prepareFirstResult() {
    if (!this.searchResults.length) {
      return;
    }
    if (this.resultIndex >= 0) {
      // skip if its already set
      return;
    }
    const filtered = appUI.getElementsInViewport(this.searchResults);
    if (filtered.length) {
      const first = filtered[0];
      const index = this.searchResults.findIndex(n => n.id === first.id);
      this.resultIndex = index - 1;
    }
  }

  styleResultNode(node,isParent) {
    const { searchRegEx, query, field } = this.getQueryDetails();
    const parent = node.getParent();
    $(`.collapsed-dot[data-id="${node.id}"]`).addClass('search-result');
    if (parent && parent !== undefined) {
      this.styleResultNode(parent,true);
      // don't render highlighted text on collapsed cards
      if (parent.isCollapsed) {
        return;
      }
    }

    if (isParent) {
      if (this.canvas.nodeTags[node.id]) {
        this.canvas.nodeTags[node.id].refs.container.classList.add('search-result-parent');
      }
    } else {
      if (this.canvas.nodeTags[node.id]) {
        this.canvas.nodeTags[node.id].refs.container.classList.add('search-result');
        if (!field || field === 'title') {
          this.highlightText(query, this.canvas.nodeTags[node.id],'title');
        }
        if (!field || field === 'summary') {
          this.highlightText(query, this.canvas.nodeTags[node.id],'summary');
        }

        if (!field || field === 'perspective') {
          this.highlightAnnotations(query, this.canvas.nodeTags[node.id], searchRegEx);
        }
        if (!field) {
          this.highlightAttributes(query, this.canvas.nodeTags[node.id], searchRegEx);
        }
      }
    }
  }

  styleResultRegion(region) {
    const { searchRegEx, query, field } = this.getQueryDetails();
    const regionTag = this.canvas.regionTags[region.id];
    regionTag.refs.main.classList.add('search-result');
    this.highlightText(query, regionTag, 'title');
  }

  getHighlightField(field) {
    if (field == 'title') {
      return 'highlightTitle';
    }
    if (field == 'summary') {
      return 'highlightSummary';
    }
  }

  highlightAttributes(query,nodeTag,searchRegEx) {
    const attributes = this.getMatchedAttributes({node: nodeTag.instance, query, searchRegEx});
    for (const attr of attributes) {
      $(`.node-attribute[data-id="${attr.id}"]`).addClass('node-attribute--search-result');
    }
  }

  highlightAnnotations(text,nodeTag,searchRegEx) {
    const perspectives = this.getMatchingAnnotations(nodeTag.instance,searchRegEx);
    for (const perspective of perspectives) {
      const annotationTag = this.canvas.nodePerspectiveAnnotations[`${nodeTag.id}-${perspective.id}`];
      let innerText = annotationTag.refs.title.innerText;
      const safeText = escapeRegExp(text);
      const replaceRegex = new RegExp(safeText,'gi');

      // highlighted text appears in a separate transparent layer over the title and summary
      // with the marked text visible & highlighted
      innerText = this.escapedHighlightedText({ innerText, replaceRegex });

      if (annotationTag && annotationTag.refs && annotationTag.refs.highlightLayer) {
        annotationTag.refs.highlightLayer.innerHTML = convertNewlines(innerText);
        annotationTag.setSearchResult(true);
        annotationTag.displayAnnotation();
      }
    }
  }

  highlightText(text,nodeTag,field) {
    const highlightField = nodeTag.refs[ this.getHighlightField(field) ];
    highlightField.innerHTML = nodeTag.refs[ field ].innerHTML;
    const marker = new Mark(highlightField);
    marker.mark(text, { className: "search-highlight", separateWordSearch: false });
  }

  escapedHighlightedText({ innerText, replaceRegex }) {
    const unique = uuid();
    const openMark = `__${unique}__OPEN_MARK__`;
    const closeMark = `__${unique}__CLOSE_MARK__`;

    innerText = innerText.replace(replaceRegex,`${openMark}$&${closeMark}`);

    innerText = htmlEscape(innerText);

    // insert our html highlighting while escaping any other html characters that may appear in this text...
    // only our highlights should have real html
    const replaceRegexO = new RegExp(openMark,'gi');
    const replaceRegexC = new RegExp(closeMark,'gi');
    innerText = innerText
      .replace(replaceRegexO,'<mark class="search-highlight">')
      .replace(replaceRegexC,'</mark>');

    return innerText;
  }

  unhighlightAnnotation(nodeTag) {
    const perspectives = pui.getNodePerspectives(nodeTag.instance);
    for (const perspective of perspectives) {
      const annotationTag = this.canvas.nodePerspectiveAnnotations[`${nodeTag.id}-${perspective.id}`];
      if (annotationTag && annotationTag.refs && annotationTag.refs.highlightLayer) {
        annotationTag.refs.highlightLayer.innerHTML = '';
        annotationTag.setSearchResult(false);
        annotationTag.hideAnnotation();
      }
    }
  }

  unhighlightText(nodeTag,field) {
    // clear out the highlight layer
    if (nodeTag && nodeTag.refs && nodeTag.refs[this.getHighlightField(field)]) {
      nodeTag.refs[this.getHighlightField(field)].innerHTML = '';
    }
  }

  unhighlightAll(elements) {
    for (const element of elements) {
      if (this.canvas.nodeTags[element.id]) {
        this.unhighlightText(this.canvas.nodeTags[element.id],'title');
        this.unhighlightText(this.canvas.nodeTags[element.id],'summary');
        this.unhighlightAnnotation(this.canvas.nodeTags[element.id]);
      }
      if (this.canvas.regionTags[element.id]) {
        this.unhighlightText(this.canvas.regionTags[element.id],'title');
      }
    }
  }

  setFocusedHighlight(element) {
    if (!element) {
      return;
    }

    const targetElement = element.getFirstVisibleAncestorNode ? element.getFirstVisibleAncestorNode() : element;
    $('.search-result-focused').removeClass('search-result-focused');
    $('.search-result-parent-focused').removeClass('search-result-parent-focused');


    if (element instanceof Node) {

      const nodeTag = this.canvas.nodeTags[targetElement.id];
      if (!nodeTag) {
        return;
      }

      if (targetElement.id === element.id) {
        nodeTag.refs.container.classList.add('search-result-focused');
      } else {
        nodeTag.refs.container.classList.add('search-result-parent-focused');

        if (this.resultCountsPerParent[targetElement.id] && nodeTag.refs.collapsedSearchResultsLabel) {
          const count = this.resultCountsPerParent[targetElement.id] || 0;
          const message = count == 1 ? `${count} collapsed result` : `${count} collapsed results`;
          nodeTag.refs.collapsedSearchResultsLabel.innerText = message;
        }
      }
    } else if (element instanceof Region) {
      const regionTag = this.canvas.regionTags[targetElement.id];
      regionTag.refs.main.classList.add('search-result-focused');
    }
  }

  targetScale() {
    const scale = this.canvas.getMatrix().scale;
    if (scale < 0.7) {
      return 0.9;
    }
    return scale;
  }

  gotoNextResult() {
    if (!this.searchResults.length) {
      return;
    }
    const previousIndex = this.resultIndex;
    this.resultIndex++;
    if (this.resultIndex >= this.searchResults.length) {
      this.unsetFocusedResult();
      this.canvas.fitMapContents();
      return;
    }

    // node: the actual search result
    const element = this.searchResults[this.resultIndex];
    // targetElement: the first visible non-collapsed parent - or the node itself if it is visible
    const targetElement = element.getFirstVisibleAncestorNode ? element.getFirstVisibleAncestorNode() : element;
    /*
       if results are collapsed and we're iterating though hidden nodes,
       iterate to a node once if it is a match, and once if it has collapsed results
       but don't repeatedly iterate to the same node if it has more than 1 collapsed result
     */
    if (this.focusedElement &&
        // current search result is collapsed within target node (so they don't match)
        element.id !== targetElement.id &&
        // the previous focused node was the current search result's first uncollapsed parent
        this.focusedElement.id === targetElement.id &&
        // previous search result doesn't match the current first uncollapsed parent
        this.searchResults[previousIndex].id !== targetElement.id &&
        // the previous uncollapsed parent was the same as the current uncollapsed parent
        this.searchResults[previousIndex].getFirstVisibleAncestorNode && this.searchResults[previousIndex].getFirstVisibleAncestorNode().id === targetElement.id) {
      return this.gotoNextResult();
    }
    this.setFocusedHighlight(element);
    this.focusedElement = targetElement;
    if (!appUI.elementIsInViewport(targetElement, appUI.centerViewportMargin()) || this.canvas.getMatrix().scale < 0.5) {
      this.canvas.panzoomToTextCenter(targetElement,this.targetScale(),{animate: true});
    }
  }

  gotoPrevResult() {
    if (!this.searchResults.length) {
      return;
    }
    const previousIndex = this.resultIndex;
    this.resultIndex--;
    if (this.resultIndex == -1) {
      this.unsetFocusedResult();
      this.canvas.fitMapContents();
      return;
    }
    if (this.resultIndex < -1) {
      this.resultIndex = this.searchResults.length -1;
      this.focusedElement = null;
    }
    const element = this.searchResults[this.resultIndex];
    const targetElement = element.getFirstVisibleAncestorNode ? element.getFirstVisibleAncestorNode() : element;

    if (this.focusedElement &&
        // current search result is collapsed within target node (so they don't match)
        element.id !== targetElement.id &&
        // the previous focused node was the same as the target node
        this.focusedElement.id === targetElement.id &&
        // previous search result doesn't match the current target node
        this.searchResults[previousIndex].id !== targetElement.id &&
        // the previous target node was the same as the current target node
        this.searchResults[previousIndex].getFirstVisibleAncestorNode && this.searchResults[previousIndex].getFirstVisibleAncestorNode().id === targetElement.id) {
      return this.gotoPrevResult();
    }

    this.setFocusedHighlight(element);
    this.focusedElement = targetElement;
    if (!appUI.elementIsInViewport(targetElement, appUI.centerViewportMargin()) || this.canvas.getMatrix().scale < 0.5) {
      this.canvas.panzoomToTextCenter(targetElement,this.targetScale(),{animate: true});
    }
  }

  dismiss() {
    this.unhighlightAll(this.searchResults);
    $('.search-result').removeClass('search-result');
    $('.node-attribute--search-result').removeClass('node-attribute--search-result');
    $('.search-result-parent').removeClass('search-result-parent');
    $('.search-result-focused').removeClass('search-result-focused');
    $('.search-result-label').text('');
    this.searchResults = [];
    this.resultIndex = -1;
    this.focusedElement = null;
    this.resultCountsPerParent = {};
    if (this.searchInput) {
      this.searchInput.value = this.currentQuery = '';
    }
    $('.text-highlight-layer').each(e => { $(e).text('');});
  }

  // show our finder instead of the browser's default
  searchKeyOverride(element, useCapture) {
    if (!element) {
      return;
    }
    element.addEventListener("keydown",(e) => {
      // take over ctrl/cmd - f when input is focused
      if (e.keyCode === 114 || ((e.ctrlKey || e.metaKey) && e.keyCode === 70)) {
        e.preventDefault();
        e.stopPropagation();
        this.searchModal.show();
      }
    }, useCapture);
  }

  // override ctrl+g to go to next, and ctrl+shift+g to go to prev result
  searchKeyIteratorOverride(element) {
    element.addEventListener("keydown",(e) => {
      // take over ctrl/cmd - f when input is focused
      if ((e.ctrlKey || e.metaKey) && e.keyCode === 71 && e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        this.gotoPrevResult();
      } else if ((e.ctrlKey || e.metaKey) && e.keyCode === 71) {
        e.preventDefault();
        e.stopPropagation();
        this.gotoNextResult();
      }
    });
  }

  // just disable ctrl-f
  disableSearchKey(element) {
    element.addEventListener("keydown",(e) => {
      // take over ctrl/cmd - f when input is focused
      if (e.keyCode === 114 || ((e.ctrlKey || e.metaKey) && e.keyCode === 70)) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
  }

  // return true if node is in sar
  isInSearchResults(nodeId) {
    return (this.searchResults.length && this.searchResults.findIndex(n => n.id === nodeId) >= 0);
  }

  isFocusedSearchResult(nodeId) {
    if (this.resultIndex >= 0 &&
        this.searchResults.length &&
        this.searchResults[this.resultIndex] &&
        this.searchResults[this.resultIndex].id == nodeId) {
      return true;
    }
    return false;
  }
}

// singleton instance
const searchUI = new SearchUI();

export { SearchUI, searchUI };



// WEBPACK FOOTER //
// ./starling/src/lib/search-ui.js